import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ExternalLink, Download, FileText, Lock, Wallet } from "lucide-react";
import { getTokenInfo } from "@/api";
import { CopyButton, addFlameToWallet } from "@/components/CopyButton";

const EXP = "https://explorer.cronos.org/address/";
const SRC = "https://repo.sourcify.dev/25/";

function AddrRow({ label, addr, tid, verified = true }) {
  return (
    <div className="flex flex-wrap items-center gap-2 py-3 border-b border-white/5" data-testid={tid}>
      <div className="w-40 text-sm text-slate-400">{label}</div>
      <code className="font-mono text-xs sm:text-sm text-slate-100 break-all flex-1">{addr}</code>
      <CopyButton value={addr} tid={`${tid}-copy`} />
      <a href={`${EXP}${addr}${verified ? "#code" : ""}`} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 underline">Explorer ↗</a>
      {verified && <a href={`${SRC}${addr}`} target="_blank" rel="noreferrer" className="text-xs text-emerald-400 underline">Sourcify ↗</a>}
    </div>
  );
}

export default function TokenPage() {
  const [t, setT] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { getTokenInfo().then(setT); }, []);
  if (!t) return <div className="pt-32 text-center text-slate-400">Loading…</div>;
  const socials = Object.entries(t.socials || {}).filter(([, v]) => v);
  const add = () => addFlameToWallet(t.contracts.flame).then(() => setMsg("FLAME added to your wallet")).catch((e) => setMsg(e.message));
  return (
    <div className="pt-24 pb-20">
      <div className="max-w-5xl mx-auto px-4">
        <div className="flex flex-wrap items-center gap-5">
          <img src="/brand/flame-256.png" alt="FLAME logo" className="w-20 h-20" data-testid="token-logo" />
          <div className="flex-1">
            <h1 className="font-display text-4xl sm:text-5xl font-black">FLAME <span className="text-slate-500 text-2xl">FLAME</span></h1>
            <div className="mt-2 flex flex-wrap gap-2 text-xs">
              <span className="chip">Cronos Mainnet · Chain 25</span><span className="chip">18 decimals</span>
              <span className="chip" data-testid="token-supply">10,000,000,000 fixed supply</span>
              <span className="chip border-emerald-500/40 text-emerald-300"><ShieldCheck size={12} /> No mint function</span>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/buy" className="btn-tactile btn-flame text-sm" data-testid="token-buy-link">Buy FLAME</Link>
            <button onClick={add} className="btn-tactile btn-ghost text-sm flex items-center gap-1" data-testid="token-add-wallet"><Wallet size={14} /> Add to wallet</button>
            <Link to="/whitepaper" className="btn-tactile btn-ghost text-sm flex items-center gap-1" data-testid="token-whitepaper-link"><FileText size={14} /> White paper</Link>
          </div>
        </div>
        {msg && <div className="mt-3 text-sm text-slate-300" data-testid="token-msg">{msg}</div>}

        <div className="mt-8 card-tile">
          <h2 className="font-display text-xl font-bold">Contracts (source verified)</h2>
          <AddrRow label="FLAME token" addr={t.contracts.flame} tid="token-addr-flame" />
          <AddrRow label="CRO Burn Splitter" addr={t.contracts.burn_splitter} tid="token-addr-splitter" />
          {t.contracts.multi_burn && <AddrRow label="Multi-coin Burn Splitter" addr={t.contracts.multi_burn} tid="token-addr-multiburn" />}
          <AddrRow label="RewardsClaim" addr={t.contracts.rewards_claim} tid="token-addr-claim" />
          {t.contracts.rewards_claim_v2 && <AddrRow label="RewardsClaim v2 (new Safe)" addr={t.contracts.rewards_claim_v2} tid="token-addr-claim-v2" />}
          {t.lock?.deployed && <AddrRow label="Liquidity Lock" addr={t.lock.address} tid="token-addr-lock" />}
          <AddrRow label="Original treasury (key destroyed)" addr={t.treasury_safe} tid="token-addr-safe" verified={false} />
          {t.new_treasury_safe && <AddrRow label="Treasury Safe" addr={t.new_treasury_safe} tid="token-addr-new-safe" verified={false} />}
        </div>

        <div className="mt-6 card-tile border-amber-500/40" data-testid="token-treasury-notice">
          <h2 className="font-display text-xl font-bold flex items-center gap-2"><Lock size={18} className="text-amber-400" /> The original treasury keeps funding rewards</h2>
          <p className="mt-2 text-sm text-slate-300">The original treasury's only key was destroyed, but it had already approved <b>about 99.99M FLAME</b> for player rewards, and claims keep paying out from it. The rest of its FLAME, its CRO and VVS pool position <b>#51663</b> stay held there for good, so the pool liquidity is effectively locked forever.</p>
          <p className="mt-2 text-sm text-slate-400">CRO burns keep sending their 1% to the original treasury. Without the key, the 99.99M reward limit can't be raised. The new treasury Safe handles everything going forward.</p>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card-tile" data-testid="token-flow">
            <h2 className="font-display text-xl font-bold">How FLAME flows</h2>
            <ul className="mt-3 text-sm text-slate-300 space-y-2 list-disc pl-5">
              <li>All 10B FLAME were minted once to the treasury Safe. No more can ever be created.</li>
              <li>Players burn CRO (99% to the dead wallet, 1% to the treasury) and earn FLAME by playing.</li>
              <li>Burn any Cronos coin with a market price. Bigger coins earn a bonus of up to 1.5x.</li>
              <li>Claims split 89% to the player, 10% stays in the treasury, 1% is burned.</li>
              <li>100M FLAME seed the VVS Finance V3 FLAME/CRO pool.</li>
            </ul>
          </div>
          <div className="card-tile" data-testid="token-market">
            <h2 className="font-display text-xl font-bold flex items-center gap-2"><Lock size={18} className="text-amber-400" /> Market & liquidity</h2>
            {t.pool.exists ? (
              <div className="mt-3 text-sm space-y-2">
                <div>Pool: <a className="text-cyan-400 underline break-all" href={`${EXP}${t.pool.address}`} target="_blank" rel="noreferrer">{t.pool.address}</a></div>
                <div>Price: <span className="font-mono text-orange-300">{t.pool.price_cro?.toPrecision(4)} CRO</span> per FLAME</div>
                <div className="flex gap-3 flex-wrap">
                  <a className="text-cyan-400 underline" href={`https://dexscreener.com/cronos/${t.pool.address}`} target="_blank" rel="noreferrer">DexScreener ↗</a>
                  <a className="text-cyan-400 underline" href={`https://www.geckoterminal.com/cronos/pools/${t.pool.address}`} target="_blank" rel="noreferrer">GeckoTerminal ↗</a>
                </div>
              </div>
            ) : <p className="mt-3 text-sm text-slate-400" data-testid="token-pool-soon">The VVS Finance V3 pool opens soon.</p>}
            <div className="mt-3 text-sm" data-testid="token-lock">Liquidity lock: {t.lock?.holds_position
              ? <><a className="text-emerald-400 underline" href={t.lock.url} target="_blank" rel="noreferrer" data-testid="token-lock-link">FlameLPLock (verified) ↗</a> · position #{t.lock.position_id} locked until <b>{new Date(t.lock.unlock_time * 1000).toISOString().slice(0, 10)}</b></>
              : t.lock_url ? <a className="text-emerald-400 underline" href={t.lock_url} target="_blank" rel="noreferrer" data-testid="token-lock-link">Lock proof ↗</a>
              : <span className="text-amber-300" data-testid="token-lock-permanent">permanent: position #{t.lock?.position_id || 51663} sits in the original treasury, whose key was destroyed, so it can never be withdrawn</span>}</div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="card-tile" data-testid="token-brand-kit">
            <h2 className="font-display text-xl font-bold">Brand kit</h2>
            <div className="mt-3 flex flex-wrap gap-2 text-xs">
              {[32, 64, 128, 256, 512].map((s) => (
                <a key={s} href={`/brand/flame-${s}.png`} download className="chip hover:brightness-125" data-testid={`token-logo-${s}`}><Download size={12} /> {s}px</a>
              ))}
              <a href="/brand/flame-logo.svg" download className="chip hover:brightness-125" data-testid="token-logo-svg"><Download size={12} /> SVG</a>
              <a href="/brand/flame-wordmark.svg" download className="chip hover:brightness-125"><Download size={12} /> Wordmark SVG</a>
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/tokenlist.json`} target="_blank" rel="noreferrer" className="chip hover:brightness-125" data-testid="token-tokenlist"><ExternalLink size={12} /> tokenlist.json</a>
            </div>
          </div>
          <div className="card-tile" data-testid="token-socials">
            <h2 className="font-display text-xl font-bold">Community</h2>
            {socials.length === 0 ? <p className="mt-3 text-sm text-slate-500">Official channels are coming soon.</p> : (
              <div className="mt-3 flex flex-wrap gap-2">{socials.map(([k, v]) => <a key={k} href={v} target="_blank" rel="noreferrer" className="chip capitalize hover:brightness-125">{k} ↗</a>)}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
